const UUID_SEGMENT = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i
const SALESFORCE_ID_SEGMENT = /^[a-zA-Z0-9]{15}(?:[a-zA-Z0-9]{3})?$/
const LONG_OPAQUE_ID_SEGMENT = /^(?=.{12,}$)(?=.*[a-zA-Z])(?=.*\d)[a-zA-Z0-9_-]+$/

interface DynamicRouteRule {
  readonly prefix: readonly string[]
  readonly segmentIndex: number
  readonly template: string
  // When set, the rule only applies if the segment immediately after
  // `segmentIndex` equals this value. Disambiguates a dynamic route from a
  // static sibling at the same depth (e.g. `/api/admin/playbooks/[slug]/export`
  // vs the static `/api/admin/playbooks/export`).
  readonly followedBy?: string
}

function routePrefix(pathname: string): readonly string[] {
  return pathname.split('/').filter(Boolean)
}

const DYNAMIC_ROUTE_RULES: readonly DynamicRouteRule[] = [
  { prefix: ['admin', 'playbooks'], segmentIndex: 2, template: '[slug]' },
  { prefix: ['admin', 'trace'], segmentIndex: 2, template: '[envelopeId]' },
  {
    prefix: ['api', 'admin', 'playbooks'],
    segmentIndex: 3,
    template: '[slug]',
    followedBy: 'export',
  },
  { prefix: ['groups'], segmentIndex: 1, template: '[groupId]' },
  { prefix: ['api', 'agent-proxy', 'deployments'], segmentIndex: 3, template: '[id]' },
  {
    prefix: routePrefix('/api/inbox/case'),
    segmentIndex: 3,
    template: '[caseId]',
    followedBy: 'account-context',
  },
  {
    prefix: routePrefix('/api/inbox/case'),
    segmentIndex: 3,
    template: '[caseId]',
    followedBy: 'contact-activity',
  },
  {
    prefix: routePrefix('/api/inbox/case'),
    segmentIndex: 3,
    template: '[caseId]',
    followedBy: 'detail-fast',
  },
  {
    prefix: routePrefix('/api/inbox/case'),
    segmentIndex: 3,
    template: '[caseId]',
    followedBy: 'detail-enrichment',
  },
  {
    prefix: routePrefix('/api/inbox/case'),
    segmentIndex: 3,
    template: '[caseId]',
    followedBy: 'conversation',
  },
  {
    prefix: routePrefix('/api/inbox/case'),
    segmentIndex: 3,
    template: '[caseId]',
    followedBy: 'active-playbook-runs',
  },
  {
    prefix: routePrefix('/api/inbox/case'),
    segmentIndex: 3,
    template: '[caseId]',
    followedBy: 'opportunity-surface',
  },
  { prefix: ['api', 'salesforce', 'connections'], segmentIndex: 3, template: '[id]' },
  { prefix: ['api', 'salesforce', 'describe'], segmentIndex: 3, template: '[object]' },
  { prefix: ['api', 'salesforce', 'sync', 'runs'], segmentIndex: 4, template: '[runId]' },
  { prefix: ['api', 'table-exports'], segmentIndex: 2, template: '[id]' },
  { prefix: ['api', 'webhooks', 'listen'], segmentIndex: 3, template: '[slug]' },
  { prefix: ['api', 'integrations', 'google', 'clients'], segmentIndex: 4, template: '[id]' },
]

function pathnameFromInput(pathname: string): string {
  const trimmed = pathname.trim()
  if (!trimmed) return '/unknown'

  try {
    const parsed = new URL(trimmed)
    return parsed.pathname || '/'
  } catch {
    // Relative paths are expected here; fall through to manual query/hash
    // stripping without treating parse failures as exceptional telemetry data.
  }

  const withoutHash = trimmed.split('#', 1)[0] ?? ''
  const withoutQuery = withoutHash.split('?', 1)[0] ?? ''
  if (!withoutQuery) return '/unknown'
  return withoutQuery.startsWith('/') ? withoutQuery : `/${withoutQuery}`
}

function sanitizeDynamicSegment(segment: string): string {
  if (
    UUID_SEGMENT.test(segment) ||
    SALESFORCE_ID_SEGMENT.test(segment) ||
    LONG_OPAQUE_ID_SEGMENT.test(segment)
  ) {
    return '[id]'
  }

  return segment
}

function routeMatchesPrefix(
  segments: readonly string[],
  prefix: readonly string[],
): boolean {
  return prefix.every((part, index) => segments[index] === part)
}

function applyDynamicRouteRules(segments: string[]): void {
  for (const rule of DYNAMIC_ROUTE_RULES) {
    if (!segments[rule.segmentIndex]) continue
    if (rule.followedBy && segments[rule.segmentIndex + 1] !== rule.followedBy) {
      continue
    }
    if (routeMatchesPrefix(segments, rule.prefix)) {
      segments[rule.segmentIndex] = rule.template
    }
  }
}

/**
 * Converts raw pathnames into privacy-safe route templates for telemetry.
 *
 * This strips query strings and hashes, templates known dynamic app routes, and
 * replaces common opaque identifier segment shapes before paths are logged or
 * sent to Web Vitals ingestion.
 */
export function sanitizePathnameForTelemetry(
  pathname: string | null | undefined,
): string {
  if (pathname == null) return '/unknown'

  const rawPathname = pathnameFromInput(pathname)
  if (rawPathname === '/unknown') return rawPathname

  const hadTrailingSlash = rawPathname.length > 1 && rawPathname.endsWith('/')
  const segments = rawPathname
    .split('/')
    .filter((segment, index) => index > 0 || segment.length > 0)

  if (segments.length === 0) return '/'

  const sanitized = segments.map((segment) => sanitizeDynamicSegment(segment))
  applyDynamicRouteRules(sanitized)

  const result = `/${sanitized.join('/')}`
  return hadTrailingSlash ? `${result}/` : result
}
